<template>
  <RevSkeleton
    v-if="pending"
    :class="{
      'md:!h-[580px]': showThumbnails,
      'md:!h-[440px]': !showThumbnails,
      '!h-[670px]': !!image,
      '!h-[470px]': !image,
    }"
    shape="rectangle"
  />

  <!-- we use v-show instead of v-if 
  When using v-if, we run into errors when lazily hydrating the components  
  Somehow, even though there is data for some carousels, some EnhancedCarousels would disappear after being hydrated
  It seems to happen when we have carousels that are used multiple times are on the same page
  Using v-show fixes the issue 
  -->
  <TitleCtaContainer
    v-show="!pending && displayedCarousels.length > 0"
    :cta
    :subtitle
    :title
  >
    <div
      class="bg-surface-default-hi rounded-lg flex flex-col md:flex-row"
      :class="showThumbnails ? 'md:h-[580px]' : 'md:h-[440px]'"
    >
      <div v-if="image" class="md:shrink-0 md:grow-0 md:basis-380">
        <ImageRenderer
          v-bind="image"
          image-class="rounded-t-lg m-auto !h-200 w-full object-cover md:!h-full md:rounded-tr-none md:rounded-bl-lg"
        />
      </div>

      <div
        class="flex grow flex-col justify-between gap-16 overflow-hidden p-16 md:gap-24 md:p-24"
      >
        <div
          v-if="showThumbnails"
          ref="thumbnails-scroller"
          class="flex grow flex-col justify-between overflow-scroll scrollbar-none"
          :class="maskClass"
        >
          <div
            :aria-label="title || ''"
            class="mx-auto flex w-max items-start gap-12 overflow-x-auto overflow-y-hidden"
            role="tablist"
          >
            <template
              v-for="(item, index) in displayedCarousels"
              :key="`${item.id}-${enhancedCarouselId}`"
            >
              <RevButtonBase
                :id="`tab-${index}`"
                ref="tabItem"
                aria-controls="enhanced-carousel-panel"
                :aria-selected="item.id === currentCarouselId"
                class="flex w-72 cursor-pointer flex-col items-center justify-center gap-4 text-center"
                :data-carousel="`carousel-${item.id}`"
                data-test="recommendation-thumbnail"
                role="tab"
                :tabindex="item.id === currentCarouselId ? 0 : -1"
                @click="() => onThumbnailClick(index)"
                @keydown.left.prevent="() => onThumbnailClick(index - 1)"
                @keydown.right.prevent="() => onThumbnailClick(index + 1)"
              >
                <ImageRenderer
                  alt=""
                  class="bg-surface-default-low rounded-lg h-48 w-72 overflow-hidden border hover:border-static-default-hi"
                  :class="
                    currentCarouselId === item.id
                      ? 'border-static-default-hi'
                      : 'border-transparent'
                  "
                  v-bind="omit(item.thumbnail, 'alt')"
                  image-class="object-cover block"
                />
                <span class="text-static-default-hi caption block">
                  {{ item.thumbnailName }}
                </span>
              </RevButtonBase>
            </template>
          </div>
        </div>

        <div
          id="enhanced-carousel-panel"
          :aria-labelledby="`tab-${currentCarouselId}`"
          role="tabpanel"
        >
          <template
            v-for="(carousel, index) in carousels"
            :key="`${enhancedCarouselId}-${carousel.id}-${carousel.recommendation.type}`"
          >
            <RefinedProducts
              v-if="carousel.recommendation.type === 'RefinedProducts'"
              v-show="carousel.id === currentCarouselId"
              v-bind="carousel.recommendation.props"
              :id="carousel.id"
              :block-position
              class="!mb-0 max-w-full"
              product-layout="slider"
              :tracking="carouselTrackingData(carousel, index)"
              @error="() => onCarouselError(carousel.id)"
            />
          </template>
        </div>
      </div>
    </div>
  </TitleCtaContainer>
</template>

<script lang="ts" setup>
import { computed, useId, useTemplateRef } from 'vue'

import type { EnhancedCarouselApiProps } from '@backmarket/http-api/src/api-specs-content/models/enhanced-product-carousel'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { omit } from '@backmarket/utils/object/omit'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevSkeleton } from '@ds/components/Skeleton'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import ImageRenderer from '../../shared-components/ImageRenderer/ImageRenderer.vue'
import TitleCtaContainer from '../../shared-components/TitleCtaContainer/TitleCtaContainer.vue'
import RefinedProducts from '../RefinedProducts/RefinedProducts.vue'

import { useBulkFetchCarousels } from './useBulkFetchCarousels'

const props = withDefaults(
  defineProps<EnhancedCarouselApiProps & BlockProps>(),
  {
    tracking: () => ({}),
  },
)

// we generate a unique id for the component to ensure that the keys in the template are unique despite multiple EnhancedCarousel with the same carousels
const enhancedCarouselId = useId()

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error', 'reset-error'])

const { currentCarouselId, displayedCarousels, pending } =
  await useBulkFetchCarousels(props.carousels)
const tabItem = useTemplateRef('tabItem')
const showThumbnails = computed(() => props.carousels.length >= 2)
const { trackClick } = useTracking()

const thumbnailsScroller = useTemplateRef<HTMLDivElement>('thumbnails-scroller')
const { maskClass } = useScrollMaskClass(thumbnailsScroller)

function onCarouselError(carouselId: string) {
  if (import.meta.server) return

  displayedCarousels.value = displayedCarousels.value.filter(
    (c) => c.id !== carouselId,
  )

  const firstCarouselNoError = displayedCarousels.value[0]

  if (!firstCarouselNoError) return

  // this is necessary to display the right carousel if the first one(s) has errors (for SSR and client-side)
  currentCarouselId.value = firstCarouselNoError.id
}

const carouselTrackingData = (
  carousel: EnhancedCarouselApiProps['carousels'][number],
  index: number,
) => {
  return {
    ...props.tracking,
    productRecoComponentPosition: index + 1,
    productComponent: `EnhancedCarousel_${carousel?.recommendation?.type}`,
  }
}

function onThumbnailClick(i: number) {
  let index = i
  if (index < 0) index = displayedCarousels.value.length - 1
  if (index >= displayedCarousels.value.length) index = 0

  tabItem.value?.[index]?.$el.focus()

  currentCarouselId.value = displayedCarousels.value[index].id

  trackClick({
    ...props.tracking,
    title: props.title || '',
    position: index + 1,
    name: props.carousels[index].thumbnailName || '',
  })
}
</script>
